import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { crmJobsAPI, customersAPI, usersAPI, userPreferencesAPI, tagsAPI } from '../lib/api';
import { jobTemplatesAPI } from '../lib/v2Api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TagMultiSelect, { TagChip, TagChips } from '../components/TagMultiSelect';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import { Plus, Search, Loader2, Briefcase, Building2, Package, FileStack, CalendarClock, Upload, Download, History, Filter, Wrench, FolderKanban, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { ListPagination } from '../components/ListPagination';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ColumnToggle } from '../components/ColumnToggle';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { CRMImportModal, ImportHistoryModal } from '../components/CRMImportModal';
import { JobStatusBadge, cleanJobNumber } from '../components/JobStatusBadge';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import MobileFAB from '../components/MobileFAB';
import { ProjectsList } from './ProjectsList';

const JOBS_COLUMN_DEFS = [
  { key: 'job_number', label: 'Job #' },
  { key: 'job_name', label: 'Job Name' },
  { key: 'customer', label: 'Customer' },
  { key: 'address', label: 'Address' },
  { key: 'status', label: 'Status' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'technician', label: 'Technician' },
  { key: 'amount', label: 'Amount' },
  { key: 'invoice_number', label: 'Invoice #' },
  { key: 'payment_status', label: 'Payment' },
];

function buildColumnState(defs, saved) {
  const state = {};
  for (const col of defs) {
    state[col.key] = { label: col.label, visible: saved ? saved[col.key] !== false : true };
  }
  return state;
}

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'paused', label: 'Paused', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
];


/* StatusBadge removed — use shared JobStatusBadge instead */


export default function Jobs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isOffice, hasEntitlement } = useAuth();
  const hasCrm = hasEntitlement('crm');
  const hcpMode = !hasCrm && hasEntitlement('hcp_jobs');
  const activeTab = hcpMode ? 'service' : (searchParams.get('tab') || 'service');

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [form, setForm] = useState({
    job_number: '', job_name: '', customer_id: '', description: '', amount: '', notes: '', template_id: '',
    job_type: '', priority: 'normal', selectedTags: [],
    scheduled_date: '', start_time: '', end_time: '', assigned_technician_id: '',
  });
  const [allJobTags, setAllJobTags] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [columns, setColumns] = useState(() => buildColumnState(JOBS_COLUMN_DEFS, null));
  const prefsFetched = useRef(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [nextJobNumber, setNextJobNumber] = useState('');
  const [fabOpen, setFabOpen] = useState(false);
  const projectsListRef = useRef(null);

  useEffect(() => {
    if (prefsFetched.current) return;
    prefsFetched.current = true;
    userPreferencesAPI.get().then((res) => {
      const saved = res.data?.jobs_table_columns;
      if (saved) setColumns(buildColumnState(JOBS_COLUMN_DEFS, saved));
    }).catch(() => {});
  }, []);

  const handleColumnToggle = useCallback((key, visible) => {
    setColumns((prev) => {
      const next = { ...prev, [key]: { ...prev[key], visible } };
      const payload = {};
      for (const k in next) payload[k] = next[k].visible;
      userPreferencesAPI.update({ jobs_table_columns: payload }).catch(() =>
        toast.error('Failed to save column preference')
      );
      return next;
    });
  }, []);

  const handleColumnReset = useCallback(() => {
    const reset = buildColumnState(JOBS_COLUMN_DEFS, null);
    setColumns(reset);
    const payload = {};
    for (const k in reset) payload[k] = true;
    userPreferencesAPI.update({ jobs_table_columns: payload }).catch(() =>
      toast.error('Failed to save column preference')
    );
  }, []);

  const isVisible = (key) => columns[key]?.visible !== false;
  const visibleColCount = Object.values(columns).filter((c) => c.visible !== false).length;

  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const sourceFilter = searchParams.get('source') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sort') || null;
  const sortDir = searchParams.get('dir') || null;
  const pageSize = 25;

  const handleSort = useCallback((key) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const curKey = p.get('sort');
      const curDir = p.get('dir');
      const DEFAULTS = { number: 'asc', name: 'asc', customer: 'asc', status: 'asc', scheduled: 'desc', amount: 'desc', invoice_number: 'asc', payment_status: 'asc', created: 'desc' };
      const defDir = DEFAULTS[key] || 'asc';
      if (curKey !== key) { p.set('sort', key); p.set('dir', defDir); }
      else if (curDir === defDir) { p.set('sort', key); p.set('dir', defDir === 'asc' ? 'desc' : 'asc'); }
      else { p.delete('sort'); p.delete('dir'); }
      p.set('page', '1');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (filterTags.length > 0) params.tag_ids = filterTags.map(t => t.id).join(',');
      if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
      const res = await crmJobsAPI.list(params);
      setJobs(res.data.jobs);
      setTotal(res.data.total);
    } catch (err) {
      if (err?.response?.data?.detail?.code === 'ENTITLEMENT_REQUIRED') {
        toast.error('CRM module is not enabled for your account.');
      } else {
        toast.error('Failed to load jobs');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sourceFilter, filterTags, sortBy, sortDir]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await customersAPI.list({ page_size: 100, is_active: true });
      setCustomers(res.data.customers || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchJobTags = useCallback(async () => {
    try {
      const res = await tagsAPI.list({ entity_type: 'job' });
      setAllJobTags(res.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchJobTags(); }, [fetchJobTags]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await jobTemplatesAPI.list();
      setTemplates(res.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const fetchTeamUsers = useCallback(async () => {
    try {
      const res = await usersAPI.list();
      setTeamUsers(Array.isArray(res.data) ? res.data.filter(u => u.is_active) : []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchTeamUsers(); }, [fetchTeamUsers]);

  const fetchNextNumber = useCallback(async () => {
    try {
      const res = await crmJobsAPI.nextNumber();
      setNextJobNumber(res.data.next_number || '');
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchNextNumber(); }, [fetchNextNumber]);

  const handleCreateTag = async (name, entityType) => {
    try {
      const res = await tagsAPI.create({ name, entity_type: entityType });
      const newTag = res.data;
      setAllJobTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      return newTag;
    } catch { return null; }
  };

  const handleSearch = (value) => {
    const params = new URLSearchParams(searchParams);
    value ? params.set('search', value) : params.delete('search');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleStatusFilter = (value) => {
    const params = new URLSearchParams(searchParams);
    value && value !== 'all' ? params.set('status', value) : params.delete('status');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSourceFilter = (value) => {
    const params = new URLSearchParams(searchParams);
    value && value !== 'all' ? params.set('source', value) : params.delete('source');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        job_number: form.job_number.trim() || null,  // null = auto-generate on backend
        job_name: form.job_name.trim() || null,
        description: form.description.trim() || null,
        amount: form.amount ? parseFloat(form.amount) : null,
        customer_id: form.customer_id || null,
        notes: form.notes.trim() || null,
        template_id: form.template_id || null,
        job_type: form.job_type.trim() || null,
        priority: form.priority || 'normal',
        tag_ids: form.selectedTags.map(t => t.id),
      };
      // Add scheduling fields only if date is provided
      if (form.scheduled_date) {
        if (!form.start_time || !form.end_time) {
          toast.error('Start and end times are required when scheduling');
          setSaving(false);
          return;
        }
        payload.scheduled_date = form.scheduled_date;
        payload.start_time = form.start_time;
        payload.end_time = form.end_time;
        payload.assigned_technician_id = form.assigned_technician_id || null;
      }
      const res = await crmJobsAPI.create(payload);
      const msg = res.data.dispatch_event_id
        ? `Job "${res.data.job_number}" created and scheduled`
        : res.data.kit_id
        ? `Job "${res.data.job_number}" created with Job Kit`
        : `Job "${res.data.job_number}" created`;
      toast.success(msg);
      setDrawerOpen(false);
      setForm({
        job_number: '', job_name: '', customer_id: '', description: '', amount: '', notes: '', template_id: '',
        job_type: '', priority: 'normal', selectedTags: [],
        scheduled_date: '', start_time: '', end_time: '', assigned_technician_id: '',
      });
      fetchJobs();
      fetchNextNumber();  // Refresh next number after creation
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const isDirty = !!(form.job_number !== nextJobNumber && form.job_number || form.job_name || form.customer_id || form.description || form.amount || form.notes || form.template_id || form.job_type || form.selectedTags.length > 0 || (form.priority && form.priority !== 'normal') || form.scheduled_date || form.start_time || form.end_time || form.assigned_technician_id);

  const handleExport = async () => {
    try {
      const res = await crmJobsAPI.exportCSV({ search, status: statusFilter });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'jobs_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="jobs-page">
        {/* Header with Service/Projects toggle */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="jobs-title">
              <Briefcase className="w-6 h-6" />
              {hcpMode ? 'Jobs (Housecall Pro)' : 'Jobs'}
            </h1>
            {activeTab === 'service' && (
              <span className="text-sm text-muted-foreground hidden sm:inline" data-testid="jobs-subtitle">
                {total} job{total !== 1 ? 's' : ''}{hcpMode ? ' synced from HCP' : ' — track work and dispatch'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Service / Projects toggle — hide in HCP mode */}
            {!hcpMode && (
            <div className="flex border rounded-lg overflow-hidden mr-2" data-testid="jobs-mode-toggle">
              <button
                onClick={() => { const p = new URLSearchParams(searchParams); p.set('tab', 'service'); setSearchParams(p, { replace: true }); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${activeTab === 'service' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted text-muted-foreground'}`}
                data-testid="jobs-tab-service"
              >
                <Wrench className="w-4 h-4" /> Service
              </button>
              <button
                onClick={() => { const p = new URLSearchParams(searchParams); p.set('tab', 'projects'); setSearchParams(p, { replace: true }); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${activeTab === 'projects' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted text-muted-foreground'}`}
                data-testid="jobs-tab-projects"
              >
                <FolderKanban className="w-4 h-4" /> Projects
              </button>
            </div>
            )}
            {activeTab === 'service' && !hcpMode && (isOwner || isOffice) && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="hidden md:inline-flex" data-testid="jobs-import-export-menu-btn">
                      <Upload className="w-4 h-4 mr-1" /> Import / Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setImportOpen(true)} data-testid="jobs-import-csv-btn">
                      <Upload className="w-4 h-4 mr-2" /> Import CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExport} data-testid="jobs-export-csv-btn">
                      <Download className="w-4 h-4 mr-2" /> Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setHistoryOpen(true)} data-testid="jobs-import-history-btn">
                      <History className="w-4 h-4 mr-2" /> Import History
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={() => {
                        setForm(f => ({ ...f, job_number: nextJobNumber }));
                        setDrawerOpen(true);
                      }} data-testid="add-job-btn" className="hidden md:inline-flex">
                        <Plus className="w-4 h-4 mr-2" /> New Job
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Create a new job to track work and dispatch.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>

        {activeTab === 'projects' ? (
          <ProjectsList ref={projectsListRef} />
        ) : (
        <>
        {/* Filters */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by job # or name..." value={search} onChange={(e) => handleSearch(e.target.value)} className="pl-10 h-10" data-testid="job-search-input" />
          </div>
          {/* Mobile: filter icon button that opens status dropdown */}
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="job-mobile-filter-btn">
                  <Filter className={`w-4 h-4 ${statusFilter ? 'text-primary' : ''}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleStatusFilter('all')} className={!statusFilter ? 'font-semibold' : ''}>
                  All Statuses
                </DropdownMenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <DropdownMenuItem key={s.value} onClick={() => handleStatusFilter(s.value)} className={statusFilter === s.value ? 'font-semibold' : ''}>
                    {s.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Desktop: full dropdown + tag filter + column toggle */}
          <div className="hidden sm:flex items-center gap-2">
            <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-[160px] h-10" data-testid="job-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {!hcpMode && hasCrm && (
              <Select value={sourceFilter || 'all'} onValueChange={handleSourceFilter}>
                <SelectTrigger className="w-[150px] h-10" data-testid="job-source-filter">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="native">Native</SelectItem>
                  <SelectItem value="housecallpro">Housecall Pro</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="w-[220px]">
              <TagMultiSelect
              entityType="job"
              selectedTags={filterTags}
              onChange={(tags) => {
                setFilterTags(tags);
                const params = new URLSearchParams(searchParams);
                params.set('page', '1');
                setSearchParams(params);
              }}
              allTags={allJobTags}
              onCreateTag={handleCreateTag}
              placeholder="Filter by tags..."
              data-testid="job-tag-filter"
            />
            </div>
          </div>
          <ColumnToggle columns={columns} onToggle={handleColumnToggle} onReset={handleColumnReset} />
        </div>

        {/* Active filter chips */}
        {(filterTags.length > 0 || statusFilter || sourceFilter) && (
          <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
            {sourceFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                Source: {sourceFilter === 'housecallpro' ? 'HCP' : sourceFilter}
                <button onClick={() => handleSourceFilter('all')} className="ml-0.5 hover:text-amber-900">&times;</button>
              </span>
            )}
            {filterTags.map(t => (
              <TagChip key={t.id} name={t.name} onRemove={() => setFilterTags(prev => prev.filter(x => x.id !== t.id))} />
            ))}
            {(filterTags.length > 0) && (
              <button
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => setFilterTags([])}
                data-testid="clear-tag-filters"
              >
                Clear tags
              </button>
            )}
          </div>
        )}

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2" data-testid="jobs-mobile-cards">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12">
              <Briefcase className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm font-medium">{search || statusFilter || filterTags.length ? 'No jobs found' : 'No jobs yet'}</p>
            </div>
          ) : jobs.map(j => (
            <div
              key={j.id}
              className="border rounded-lg p-3.5 active:bg-muted/50"
              onClick={() => navigate(`/jobs/${j.id}`)}
              data-testid={`job-card-${j.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm">{cleanJobNumber(j.job_number)}</span>
                    <JobStatusBadge status={j.status} />
                    {j.source_system === 'housecallpro' && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800" data-testid={`job-hcp-badge-mobile-${j.id}`}>HCP</span>}
                    {j.job_type === 'maintenance' && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-violet-100 text-violet-700">Maintenance</span>}
                  </div>
                  {j.job_name && (
                    <TruncatedTextWithTooltip
                      as="p"
                      text={j.job_name}
                      className="text-xs text-muted-foreground"
                    />
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">{j.customer_name || 'No customer'}</p>
                  {j.scheduled_date && <p className="text-[11px] text-muted-foreground mt-1">{new Date(j.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>}
                </div>
                {j.amount > 0 && <span className="text-sm font-bold shrink-0">${j.amount.toFixed(2)}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="border rounded-lg overflow-x-auto hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                {isVisible('job_number') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="number" onSort={handleSort} className="min-w-[120px]">Job #</SortableTableHead>}
                {isVisible('job_name') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="name" onSort={handleSort} className="min-w-[140px] hidden md:table-cell">Job Name</SortableTableHead>}
                {isVisible('customer') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="customer" onSort={handleSort} className="min-w-[120px] hidden lg:table-cell">Customer</SortableTableHead>}
                {isVisible('address') && <TableHead className="min-w-[150px] hidden xl:table-cell">Address</TableHead>}
                {isVisible('status') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="status" onSort={handleSort} className="w-28">Status</SortableTableHead>}
                {isVisible('scheduled') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="scheduled" onSort={handleSort} className="min-w-[100px] hidden md:table-cell">Scheduled</SortableTableHead>}
                {isVisible('technician') && <TableHead className="min-w-[110px] hidden lg:table-cell">Technician</TableHead>}
                {isVisible('amount') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="amount" onSort={handleSort} className="min-w-[90px] hidden xl:table-cell text-right">Amount</SortableTableHead>}
                {isVisible('invoice_number') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="invoice_number" onSort={handleSort} className="min-w-[100px] hidden xl:table-cell">Invoice #</SortableTableHead>}
                {isVisible('payment_status') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="payment_status" onSort={handleSort} className="min-w-[100px] hidden xl:table-cell">Payment</SortableTableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Briefcase className="w-10 h-10 text-muted-foreground/40" />
                      <p className="text-sm font-medium">{search || statusFilter || filterTags.length ? 'No jobs found' : 'No jobs yet'}</p>
                      <p className="text-xs text-muted-foreground">{search || statusFilter || filterTags.length ? 'Try adjusting your filters.' : hcpMode ? 'Import jobs from Housecall Pro to see them here.' : 'Create your first job to get started.'}</p>
                      {!search && !statusFilter && !filterTags.length && !hcpMode && (isOwner || isOffice) && (
                        <Button size="sm" className="mt-2" onClick={() => setDrawerOpen(true)} data-testid="empty-add-job-btn"><Plus className="w-4 h-4 mr-1" /> New Job</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((j) => (
                  <TableRow
                    key={j.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/jobs/${j.id}`)}
                    data-testid={`job-row-${j.id}`}
                  >
                    {isVisible('job_number') && (
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-sm" data-testid={`job-number-${j.id}`}>{cleanJobNumber(j.job_number)}</span>
                          {j.source_system === 'housecallpro' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800" data-testid={`job-hcp-badge-${j.id}`}>HCP</span>
                          )}
                        </div>
                        {j.template_name && (
                          <div className="text-[11px] text-muted-foreground/70 flex items-center gap-0.5 mt-0.5"><FileStack className="w-3 h-3" /> {j.template_name}</div>
                        )}
                      </TableCell>
                    )}
                    {isVisible('job_name') && (
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-1.5">
                          <TruncatedTextWithTooltip
                            text={j.job_name || '—'}
                            className="text-sm block max-w-[180px]"
                            data-testid={`job-name-${j.id}`}
                          />
                          {j.job_type === 'maintenance' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-violet-100 text-violet-700 shrink-0" data-testid={`job-type-badge-${j.id}`}>Maintenance</span>
                          )}
                        </div>
                        {j.tag_objects && j.tag_objects.length > 0 && (
                          <div className="mt-1"><TagChips tags={j.tag_objects} /></div>
                        )}
                      </TableCell>
                    )}
                    {isVisible('customer') && (
                      <TableCell className="hidden lg:table-cell">
                        <TruncatedTextWithTooltip
                          text={j.customer_name || '—'}
                          className="text-sm block max-w-[140px]"
                          data-testid={`job-customer-${j.id}`}
                        />
                      </TableCell>
                    )}
                    {isVisible('address') && (
                      <TableCell className="hidden xl:table-cell">
                        <TruncatedTextWithTooltip
                          text={j.customer_address || '—'}
                          className="text-xs text-muted-foreground block max-w-[180px]"
                          data-testid={`job-address-${j.id}`}
                        />
                      </TableCell>
                    )}
                    {isVisible('status') && (
                      <TableCell>
                        <JobStatusBadge status={j.status} />
                      </TableCell>
                    )}
                    {isVisible('scheduled') && (
                      <TableCell className="hidden md:table-cell">
                        <span className="text-sm" data-testid={`job-scheduled-${j.id}`}>
                          {j.scheduled_date ? new Date(j.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </span>
                      </TableCell>
                    )}
                    {isVisible('technician') && (
                      <TableCell className="hidden lg:table-cell">
                        <TruncatedTextWithTooltip
                          text={j.assigned_technician || '—'}
                          className="text-sm block max-w-[120px]"
                          data-testid={`job-technician-${j.id}`}
                        />
                      </TableCell>
                    )}
                    {isVisible('amount') && (
                      <TableCell className="hidden xl:table-cell text-right">
                        <span className="text-sm font-medium" data-testid={`job-amount-${j.id}`}>
                          {j.amount != null ? `$${parseFloat(j.amount).toFixed(2)}` : j.job_amount_cents != null ? `$${(j.job_amount_cents / 100).toFixed(2)}` : '—'}
                        </span>
                      </TableCell>
                    )}
                    {isVisible('invoice_number') && (
                      <TableCell className="hidden xl:table-cell">
                        <span className="text-sm" data-testid={`job-invoice-${j.id}`}>{j.invoice_number || '—'}</span>
                      </TableCell>
                    )}
                    {isVisible('payment_status') && (
                      <TableCell className="hidden xl:table-cell">
                        {j.payment_status ? (
                          <Badge variant={j.payment_status === 'paid' ? 'default' : 'secondary'} className={`text-xs ${j.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : j.payment_status === 'overdue' ? 'bg-red-100 text-red-800' : ''}`} data-testid={`job-payment-${j.id}`}>{j.payment_status}</Badge>
                        ) : <span className="text-sm text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <ListPagination page={page} total={total} pageSize={pageSize} totalPages={totalPages} onPageChange={handlePageChange} showPageSize={false} />
        )}

        {/* Create Job Drawer */}
        <AppDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          title="New Job"
          description="Create a new job to track work."
          isDirty={isDirty}
          data-testid="create-job-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button type="submit" form="create-job-form" disabled={saving} data-testid="save-job-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Job
              </Button>
            </>
          }
        >
          <form id="create-job-form" onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="job_number">Job Number</Label>
                <Input id="job_number" value={form.job_number} onChange={(e) => setForm({ ...form, job_number: e.target.value })} placeholder={nextJobNumber || 'Auto-generated'} data-testid="input-job-number" />
                <p className="text-xs text-muted-foreground mt-1">Auto-generated. Edit to override.</p>
              </div>
              <div>
                <Label htmlFor="job_name">Job Name</Label>
                <Input id="job_name" value={form.job_name} onChange={(e) => setForm({ ...form, job_name: e.target.value })} placeholder="e.g. HVAC Install" data-testid="input-job-name" />
              </div>
            </div>
            <div>
              <Label>Customer (optional)</Label>
              <Select value={form.customer_id || 'none'} onValueChange={(v) => setForm({ ...form, customer_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="select-customer">
                  <SelectValue placeholder="No customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No customer</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="job_description">Description</Label>
                <Textarea id="job_description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Scope of work..." rows={2} data-testid="input-job-description" />
              </div>
              <div>
                <Label htmlFor="job_amount">Amount ($)</Label>
                <Input id="job_amount" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="input-job-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="job_type">Job Type</Label>
                <Input id="job_type" value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })} placeholder="e.g. Install, Repair, Service" data-testid="input-job-type" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority || 'normal'} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="select-priority">
                    <SelectValue placeholder="Normal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Tags</Label>
              <TagMultiSelect
                entityType="job"
                selectedTags={form.selectedTags}
                onChange={(tags) => setForm({ ...form, selectedTags: tags })}
                allTags={allJobTags}
                onCreateTag={handleCreateTag}
                data-testid="input-job-tags"
              />
            </div>
            <div>
              <Label>Job Template (optional)</Label>
              <p className="text-xs text-muted-foreground mb-1">Select a template to auto-generate a Job Kit with required parts</p>
              <Select value={form.template_id || 'none'} onValueChange={(v) => setForm({ ...form, template_id: v === 'none' ? '' : v })}>
                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                  <SelectTrigger data-testid="select-template">
                    <SelectValue placeholder="No template" />
                  </SelectTrigger>
                </TooltipTrigger><TooltipContent>Choose a template to pre-fill the parts list for this job.</TooltipContent></Tooltip></TooltipProvider>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.item_count} items)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="job_notes">Notes</Label>
              <Textarea id="job_notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Job details..." rows={3} data-testid="input-job-notes" />
            </div>

            {/* Optional Scheduling Section */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Schedule (optional)</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Fill in to auto-create a dispatch event. Leave blank to create an unscheduled job.</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="scheduled_date">Date</Label>
                  <Input id="scheduled_date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="input-scheduled-date" />
                </div>
                <div>
                  <Label htmlFor="start_time">Start Time</Label>
                  <Input id="start_time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="input-start-time" />
                </div>
                <div>
                  <Label htmlFor="end_time">End Time</Label>
                  <Input id="end_time" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="input-end-time" />
                </div>
              </div>
              <div className="mt-3">
                <Label>Assigned Technician</Label>
                <Select value={form.assigned_technician_id || 'none'} onValueChange={(v) => setForm({ ...form, assigned_technician_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="select-technician">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {teamUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </form>
        </AppDrawer>

        <CRMImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          entityType="jobs"
          onImportComplete={fetchJobs}
        />
        <ImportHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="jobs"
          onRollbackComplete={fetchJobs}
        />
        </>
        )}
      </div>
      {(isOwner || isOffice) && !hcpMode && (
        <MobileFABContainer data-testid="mobile-fab-container">
          <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
            {activeTab === 'service' ? (
              <>
                <MobileFABMenuItem icon={Plus} onClick={() => { setFabOpen(false); setForm(f => ({ ...f, job_number: nextJobNumber })); setDrawerOpen(true); }} data-testid="fab-new-job">
                  New Job
                </MobileFABMenuItem>
                <MobileFABMenuItem icon={Upload} onClick={() => { setFabOpen(false); setImportOpen(true); }} data-testid="fab-import">
                  Import
                </MobileFABMenuItem>
              </>
            ) : (
              <>
                <MobileFABMenuItem icon={Plus} onClick={() => { setFabOpen(false); projectsListRef.current?.openNewProject(); }} data-testid="fab-new-project">
                  New Project
                </MobileFABMenuItem>
                <MobileFABMenuItem icon={FolderKanban} onClick={() => { setFabOpen(false); projectsListRef.current?.startAddStage(); }} data-testid="fab-add-stage">
                  Add Stage
                </MobileFABMenuItem>
                <MobileFABMenuItem icon={Settings2} onClick={() => { setFabOpen(false); projectsListRef.current?.openCardLayout(); }} data-testid="fab-card-layout">
                  Card Layout
                </MobileFABMenuItem>
              </>
            )}
          </MobileFABMenu>
          <MobileFAB onClick={() => setFabOpen(o => !o)} rotating={fabOpen} data-testid="mobile-fab-job" />
        </MobileFABContainer>
      )}
    </Layout>
  );
}
